import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, Chip, TextField, Tooltip } from '@mui/material';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { API, type TeamManagersResponse } from '../../../../api';
import { getLLAuthHeaders } from '../../utils/llAuthHeaders';
import { useProgramContext } from '../../../../hooks/useProgramContext';
import DosenButton from '../../../../components/ui/Buttons/DosenButton';
import { toOrgMember } from '../Program/components/learning-lifecycle/useAdminLearningLifecycle';
import type { ProgramOwner } from '../Program/components/learning-lifecycle/types';
import { inputSx, menuPaperSx, pendingChipSx, textActionSx, valueChipSx } from './orgSettingsStyles';
import { chipDeleteIcon, popupIcon, renderCheckOption } from './orgSettingsInputs';

// Department managers — each row of Department in Audience Attributes on
// Organization Settings has its managers. Every department needs one. A
// team's managers become admins of every program the team is an audience of,
// now and in future, and their dashboard shows that team. Pick existing
// members, or invite someone new: they get a normal member invite and become
// the team's manager when they sign up. Org admins manage every team already,
// so they are not listed. Picking managers is a pending change of the
// Audience Attributes card: its Save Changes writes them, Cancel Changes puts
// them back. An invite is sent (and recorded on the team) straight away.

export interface Team {
  id: string;
  name: string;
  managerIds: string[];
  pendingEmails: string[];
}

// A picker entry: a member, or someone invited who hasn't signed up.
type Pick = ProgramOwner & { pendingEmail?: string };

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The org's teams (Department's saved values) with their managers, the org's
 * members to pick from, and the saves. Reloads when `teamKey` changes.
 */
export const useTeamManagers = (orgAuthId: string | undefined, teamKey: string) => {
  const { location } = useProgramContext();
  const [loading, setLoading] = useState(true);
  // `teams` is what the pickers show (with unsaved picks), `saved` what the BE holds.
  const [teams, setTeams] = useState<Team[]>([]);
  const [saved, setSaved] = useState<Team[]>([]);
  const [orgAdminIds, setOrgAdminIds] = useState<string[]>([]);
  // The list the BE treats as the Teams list (flagged, else named "Teams"),
  // null when the org has none; undefined until the GET answers.
  const [teamsList, setTeamsList] = useState<TeamManagersResponse['teamsList'] | undefined>(undefined);
  const [members, setMembers] = useState<ProgramOwner[]>([]);
  const [memberIdByEmail, setMemberIdByEmail] = useState<Map<string, string>>(new Map());
  const [savingId, setSavingId] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!orgAuthId) return undefined;
    let alive = true;
    const load = async () => {
      setLoading(true);
      const headers = getLLAuthHeaders();
      const [managersR, membersR] = await Promise.allSettled([
        API.getTeamManagers({ orgId: orgAuthId, ...headers, location }),
        API.getUsersByOrgId(headers.authorization, orgAuthId)
      ]);
      if (!alive) return;
      const memberList: any[] =
        membersR.status === 'fulfilled' && Array.isArray(membersR.value?.data) ? membersR.value.data : [];
      setMembers(
        memberList
          .map(toOrgMember)
          .filter((m) => m.id)
          .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
      );
      setMemberIdByEmail(
        new Map(
          memberList
            .filter((u) => u?.email && (u.id || u.user_id))
            .map((u) => [String(u.email).trim().toLowerCase(), String(u.id || u.user_id)])
        )
      );
      if (managersR.status === 'fulfilled') {
        const loaded = managersR.value.data.teams.map((t) => ({ ...t, pendingEmails: t.pendingEmails || [] }));
        setTeams(loaded);
        setSaved(loaded);
        setOrgAdminIds(managersR.value.data.orgAdminIds);
        setTeamsList(managersR.value.data.teamsList ?? null);
      } else {
        toast.error(
          _.get(
            managersR.reason,
            'response.data.message',
            'Department managers could not be loaded. Refresh the page and try again.'
          )
        );
      }
      setLoading(false);
    };
    load();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgAuthId, location, teamKey, reloadTick]);

  const reload = () => setReloadTick((n) => n + 1);

  const memberById = new Map(members.map((m) => [m.id, m]));

  // A department has its own manager once someone besides the org admins
  // manages it, or has been invited to.
  const needsManager = (team: Team) =>
    team.managerIds.filter((id) => !orgAdminIds.includes(id)).length === 0 && team.pendingEmails.length === 0;

  const sameManagers = (a: Team, b?: Team) =>
    Boolean(b) &&
    _.isEqual([...a.managerIds].sort(), [...b!.managerIds].sort()) &&
    _.isEqual([...a.pendingEmails].sort(), [...b!.pendingEmails].sort());
  const changedTeams = teams.filter(
    (t) =>
      !sameManagers(
        t,
        saved.find((s) => s.id === t.id)
      )
  );
  const dirty = changedTeams.length > 0;

  // A pick in a department's picker: shown at once, saved with the card.
  const setTeamManagers = (team: Team, managerIds: string[], pendingEmails: string[]) =>
    setTeams((prev) => prev.map((t) => (t.id === team.id ? { ...t, managerIds, pendingEmails } : t)));

  const putTeam = (team: Team) =>
    API.updateTeamManagers({
      orgId: orgAuthId as string,
      valueId: team.id,
      managerIds: team.managerIds,
      pendingEmails: team.pendingEmails,
      ...getLLAuthHeaders(),
      location
    });

  /** Save Changes: writes every department whose managers changed. True when all saved. */
  const saveAll = async () => {
    if (!orgAuthId) return false;
    let ok = true;
    for (const team of changedTeams) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await putTeam(team);
        setSaved((prev) => prev.map((s) => (s.id === team.id ? team : s)));
      } catch (e) {
        ok = false;
        toast.error(_.get(e, 'response.data.message', `${team.name}'s managers could not be saved. Try again.`));
      }
    }
    return ok;
  };

  /** Cancel Changes: back to the saved managers. */
  const cancel = () => setTeams(saved);

  const inviteManager = async (team: Team, invitee: { firstName: string; lastName: string; email: string }) => {
    if (!orgAuthId) return false;
    const email = invitee.email.trim().toLowerCase();
    const existingId = memberIdByEmail.get(email);
    const existing = existingId ? memberById.get(existingId) : undefined;
    if (existing) {
      toast.info(`${existing.name} is already in your organization. Pick them from the list.`);
      return false;
    }
    // Record the invite on the team's SAVED managers first, so a sent invite
    // can't be lost to a failed save (they'd sign up as a plain member), and
    // unsaved picks stay unsaved. Then send it; if that fails, take the email
    // off again.
    const savedTeam = saved.find((s) => s.id === team.id) || team;
    const recorded = { ...savedTeam, pendingEmails: Array.from(new Set([...savedTeam.pendingEmails, email])) };
    const addPending = (list: Team[], next: Team) =>
      list.map((t) =>
        t.id === team.id ? { ...t, pendingEmails: Array.from(new Set([...t.pendingEmails, ...next.pendingEmails])) } : t
      );
    setSavingId(team.id);
    try {
      await putTeam(recorded);
    } catch (e) {
      toast.error(_.get(e, 'response.data.message', `${team.name}'s managers could not be saved. Try again.`));
      setSavingId(null);
      return false;
    }
    setSaved((prev) => prev.map((s) => (s.id === team.id ? recorded : s)));
    setTeams((prev) => addPending(prev, recorded));
    try {
      // A plain member invite: they become this team's manager, and so an
      // admin of its programs, when they sign up. Inviting as "admin" would
      // make them an org admin, who sees every team.
      await API.inviteOrgMember(orgAuthId, { ...invitee, email }, location);
    } catch (e) {
      toast.error(_.get(e, 'response.data.message', `${email} could not be invited. Try again.`));
      try {
        await putTeam(savedTeam);
        setSaved((prev) => prev.map((s) => (s.id === team.id ? savedTeam : s)));
        setTeams((prev) =>
          prev.map((t) => (t.id === team.id ? { ...t, pendingEmails: t.pendingEmails.filter((p) => p !== email) } : t))
        );
      } catch {
        /* the invite stays recorded; the admin can remove it and save */
      }
      setSavingId(null);
      return false;
    }
    setSavingId(null);
    toast.success(`Invited ${invitee.firstName}. They'll manage ${team.name} once they sign up.`);
    return true;
  };

  return {
    loading,
    teams,
    teamsList,
    orgAdminIds,
    members,
    memberById,
    savingId,
    needsManager,
    dirty,
    setTeamManagers,
    saveAll,
    cancel,
    inviteManager,
    reload
  };
};

export type TeamManagers = ReturnType<typeof useTeamManagers>;

export const InviteManagerForm: React.FC<{
  busy: boolean;
  onInvite: (v: { firstName: string; lastName: string; email: string }) => void;
  onCancel: () => void;
}> = ({ busy, onInvite, onCancel }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const valid = firstName.trim() && lastName.trim() && EMAIL.test(email.trim());
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
      <TextField
        size="small"
        placeholder="First name"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        sx={{ ...inputSx, width: 140 }}
      />
      <TextField
        size="small"
        placeholder="Last name"
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
        sx={{ ...inputSx, width: 140 }}
      />
      <TextField
        size="small"
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        sx={{ ...inputSx, flex: 1, minWidth: 180 }}
      />
      <DosenButton sizeVariant="sm" tone="basic" disabled={busy} onClick={onCancel}>
        Cancel
      </DosenButton>
      <DosenButton
        sizeVariant="sm"
        tone="cta"
        disabled={!valid}
        loading={busy}
        loadingText="Inviting..."
        onClick={() =>
          onInvite({ firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim().toLowerCase() })
        }
      >
        Invite
      </DosenButton>
    </Box>
  );
};

/** One department's managers: the picker, and "Invite new manager" under it. */
export const ManagerPicker: React.FC<{ team: Team; managers: TeamManagers }> = ({ team, managers }) => {
  const { orgAdminIds, members, memberById, savingId, setTeamManagers, inviteManager, needsManager } = managers;
  const [inviting, setInviting] = useState(false);
  // Org admins manage every department already, so they are neither shown
  // nor offered here: the box holds only this department's own managers.
  const value: Pick[] = [
    ...team.managerIds
      .filter((id) => !orgAdminIds.includes(id))
      .map((id) => memberById.get(id) || { id, name: 'Person' }),
    ...team.pendingEmails.map((email) => ({ id: `pending:${email}`, name: email, pendingEmail: email }))
  ];
  const busy = savingId === team.id;
  const required = needsManager(team);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
      <Autocomplete<Pick, true, true>
        multiple
        disableClearable
        popupIcon={popupIcon}
        sx={{
          ...inputSx,
          ...(required && {
            '& .MuiOutlinedInput-root:not(.Mui-focused) fieldset': { borderColor: 'rgba(247,148,32,0.6)' }
          })
        }}
        options={members.filter((m) => !orgAdminIds.includes(m.id))}
        value={value}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        onChange={(_event, next) =>
          setTeamManagers(
            team,
            next.filter((m) => !m.pendingEmail && !orgAdminIds.includes(m.id)).map((m) => m.id),
            next.filter((m) => m.pendingEmail).map((m) => m.pendingEmail as string)
          )
        }
        componentsProps={{ paper: { sx: menuPaperSx } }}
        renderOption={(props, option, { selected }) => renderCheckOption(props, option.name, selected, option.id)}
        renderTags={(tags, getTagProps) =>
          tags.map((option, index) => {
            const { onDelete, ...tagProps } = getTagProps({ index });
            const chip = (
              <Chip
                {...tagProps}
                key={option.id}
                label={option.pendingEmail ? `${option.name} · Invited` : option.name}
                onDelete={onDelete}
                deleteIcon={chipDeleteIcon}
                sx={option.pendingEmail ? pendingChipSx : valueChipSx}
              />
            );
            if (option.pendingEmail) {
              return (
                <Tooltip key={option.id} title="Invited. They become this department's manager when they sign up.">
                  {chip}
                </Tooltip>
              );
            }
            return chip;
          })
        }
        renderInput={(params) => <TextField {...params} size="small" placeholder="Add a manager" fullWidth />}
      />
      {inviting ? (
        <InviteManagerForm
          busy={busy}
          onCancel={() => setInviting(false)}
          onInvite={async (invitee) => {
            if (await inviteManager(team, invitee)) setInviting(false);
          }}
        />
      ) : (
        <Box>
          <Box component="button" type="button" onClick={() => setInviting(true)} sx={textActionSx}>
            Invite new manager
          </Box>
        </Box>
      )}
    </Box>
  );
};
